import React from 'react';
import {
  AppBar,
  Box,
  IconButton,
  styled,
  SxProps,
  Theme,
  Toolbar,
  Typography,
  TypographyProps,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { BackButton } from '../BackButton/BackButton';

const StyledTypography = styled(Typography)(({ theme }) => ({
  textWrap: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  padding: `0 ${theme.spacing(1)}`,
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.grey[800],
}));

export interface SearchHeaderBarProps {
  title: string;
  onClickBack: () => void;
  showBackButton?: boolean;
  titleProps?: TypographyProps;
  headerStyles?: SxProps<Theme>;
  className?: string;
  showSearch?: boolean;
  onClickSearch?: () => void;
  searchAriaLabel?: string;
  titleAlign?: 'center' | 'left';
  disableElevation?: boolean;
}

export const SearchHeaderBar = ({
  title,
  onClickBack,
  showBackButton = true,
  titleProps,
  headerStyles,
  className = 'search-header',
  showSearch = false,
  onClickSearch,
  searchAriaLabel = 'Search',
  titleAlign = 'left',
  disableElevation = false,
}: SearchHeaderBarProps) => (
  <StyledAppBar
    position="sticky"
    className={className}
    sx={headerStyles}
    elevation={disableElevation ? 0 : undefined}
  >
    <Toolbar
      disableGutters
      sx={{ justifyContent: 'space-between', px: 1.75 }}
      className={`${className}__toolbar`}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {showBackButton && (
          <BackButton
            className={`${className}__back-button`}
            onClick={onClickBack}
            color="inherit"
          />
        )}
      </Box>

      <StyledTypography
        className={`${className}__title`}
        aria-label={title}
        variant="h6"
        fontWeight="normal"
        color="inherit"
        sx={{
          flex: 1,
          minWidth: 0, // lets ellipsis work inside the flex row
          textAlign: titleAlign,
          // When left-aligned, sit next to the back button rather than centered.
          ...(titleAlign === 'left' && { pl: 0.5 }),
        }}
        {...titleProps}
      >
        {title}
      </StyledTypography>

      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {showSearch && (
          <IconButton
            className={`${className}__search-button`}
            type="button"
            onClick={onClickSearch}
            color="inherit"
            aria-label={searchAriaLabel}
          >
            <SearchIcon aria-hidden focusable={false} />
          </IconButton>
        )}
      </Box>
    </Toolbar>
  </StyledAppBar>
);
